import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CompanyData,
  CompanySettingsData,
  CompanyStructureDataResponse,
  LabelColors,
  Response,
  UpdateCompanyRequest
} from '../interfaces';
import { BILLING_STATUS, CALENDAR_EVENT_TYPE, COMPANY_SETTINGS_INSTANCE } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  labelColors$: BehaviorSubject<LabelColors[]> = new BehaviorSubject<LabelColors[]>([]);
  inventoryTypeData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);
  inventoryStatusData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);
  employeesPositionData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);
  employeesStatusData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);
  employeesAvailabilityData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);
  employeesProjectData$ = new BehaviorSubject<CompanySettingsData[] | null>(null);

  isDevicesUpToDate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isEmployeesUpToDate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  companyInfo$: BehaviorSubject<CompanyData | null> = new BehaviorSubject<CompanyData | null>(null);

  constructor(private readonly http: HttpClient) {}

  getCompanyInfo() {
    this.getCompanyInfoRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.companyInfo$.next(response.data);
        }
      }
    });
  }

  isSubscriptionInactive(): boolean {
    const company = this.companyInfo$.getValue()
    return company?.billing?.status === BILLING_STATUS.SUSPENDED || company?.billing?.status === BILLING_STATUS.UNSUBSCRIBED
  }

  isEmployeesLimitExceed(): boolean {
    const company = this.companyInfo$.getValue();
    return company?.billing?.status === BILLING_STATUS.SMALL && company.activeEmployeesCount > 49;
  }

  getCompanyInfoRequest(): Observable<Response<CompanyData>> {
    return this.http.get<Response<CompanyData>>(`${environment.baseUrl}/company`);
  }

  getCompanyStructureRequest(): Observable<Response<CompanyStructureDataResponse>> {
    return this.http.get<Response<CompanyStructureDataResponse>>(`${environment.baseUrl}/company/structure`);
  }

  updateCompanyRequest(data: UpdateCompanyRequest, file: File | null = null, logoRemoved: boolean = false): Observable<Response<CompanyData>> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });

    formData.append('request', jsonBlob);
    if (file) {
      formData.append('logo', file);
    } else if (logoRemoved) {
      const emptyFile = new File([''], '');
      formData.append('logo', emptyFile);
    }

    return this.http.put<Response<CompanyData>>(`${environment.baseUrl}/company`, formData);
  }

  updatePolicyFileRequest(file: File | null, fileRemoved: boolean, eventType: CALENDAR_EVENT_TYPE): Observable<Response<CompanyData>> {
    const formData = new FormData();

    if (file) {
      formData.append('file', file);
    } else if (fileRemoved) {
      const emptyFile = new File([''], '');
      formData.append('file', emptyFile);
    }

    return this.http.put<Response<CompanyData>>(`${environment.baseUrl}/company/policy?type=${eventType}`, formData);
  }

  getLabelColorDataById(id: string) {
    return this.labelColors$.getValue().find((val: LabelColors) => val.id === id);
  }

  updateLabelColorsList() {
    this.getLabelColorsRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.labelColors$.next(response.data);
        }
      }
    });
  }

  updateInventoryTypeList() {
    this.getInventoryTypeRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.inventoryTypeData$.next(response.data);
        }
      }
    });
  }

  updateInventoryStatusList() {
    this.getInventoryStatusRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.inventoryStatusData$.next(response.data);
        }
      }
    });
  }

  updateEmployeePositionList() {
    this.getEmployeePositionRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.employeesPositionData$.next(response.data);
        }
      }
    });
  }

  updateEmployeeStatusList() {
    this.getEmployeeStatusRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.employeesStatusData$.next(response.data);
        }
      }
    });
  }

  updateEmployeeAvailabilityList() {
    this.getEmployeeAvailabilityRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.employeesAvailabilityData$.next(response.data);
        }
      }
    });
  }

  updateEmployeeProjectList() {
    this.getEmployeeProjectRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.employeesProjectData$.next(response.data);
        }
      }
    });
  }

  updateListForInstance(instance: COMPANY_SETTINGS_INSTANCE): void {
    switch (instance) {
      case COMPANY_SETTINGS_INSTANCE.INVENTORY_TYPE:
        this.updateInventoryTypeList();
        break;
      case COMPANY_SETTINGS_INSTANCE.INVENTORY_STATUS:
        this.updateInventoryStatusList();
        break;
      case COMPANY_SETTINGS_INSTANCE.EMPLOYEE_POSITION:
        this.updateEmployeePositionList();
        break;
      case COMPANY_SETTINGS_INSTANCE.EMPLOYEE_STATUS:
        this.updateEmployeeStatusList();
        break;
      case COMPANY_SETTINGS_INSTANCE.EMPLOYEE_AVAILABILITY:
        this.updateEmployeeAvailabilityList();
        break;
      case COMPANY_SETTINGS_INSTANCE.EMPLOYEE_PROJECT:
        this.updateEmployeeProjectList();
        break;
      default:
        break;
    }
  }

  getLabelColorsRequest(): Observable<Response<LabelColors[]>> {
    return this.http.get<Response<LabelColors[]>>(`${environment.baseUrl}/info/label-colors`);
  }

  getInventoryTypeRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/device/type`);
  }

  createInventoryTypeRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/device/type`, data);
  }

  updateInventoryTypeRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/device/type/${id}`, data);
  }

  removeInventoryTypeRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/device/type/${id}`);
  }

  getInventoryStatusRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/device/status`);
  }

  createInventoryStatusRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/device/status`, data);
  }

  updateInventoryStatusRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/device/status/${id}`, data);
  }

  removeInventoryStatusRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/device/status/${id}`);
  }

  getEmployeePositionRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/employee/position`);
  }

  createEmployeePositionRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/position`, data);
  }

  updateEmployeePositionRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/position/${id}`, data);
  }

  removeEmployeePositionRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/employee/position/${id}`);
  }

  getEmployeeStatusRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/employee/status`);
  }

  createEmployeeStatusRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/status`, data);
  }

  updateEmployeeStatusRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/status/${id}`, data);
  }

  removeEmployeeStatusRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/employee/status/${id}`);
  }

  getEmployeeAvailabilityRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/employee/availability`);
  }

  createEmployeeAvailabilityRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/availability`, data);
  }

  updateEmployeeAvailabilityRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/employee/availability/${id}`, data);
  }

  removeEmployeeAvailabilityRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/employee/availability/${id}`);
  }

  getEmployeeProjectRequest(): Observable<Response<CompanySettingsData[]>> {
    return this.http.get<Response<CompanySettingsData[]>>(`${environment.baseUrl}/company/settings/project`);
  }

  createEmployeeProjectRequest(data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.post<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/project`, data);
  }

  updateEmployeeProjectRequest(id: number, data: CompanySettingsData): Observable<Response<CompanySettingsData>> {
    return this.http.put<Response<CompanySettingsData>>(`${environment.baseUrl}/company/settings/project/${id}`, data);
  }

  removeEmployeeProjectRequest(id: number): Observable<Response<any>> {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/company/settings/project/${id}`);
  }

  clearData() {
    this.isEmployeesUpToDate$.next(false);
    this.employeesPositionData$.next(null);
    this.employeesStatusData$.next(null);
    this.employeesAvailabilityData$.next(null);
    this.employeesProjectData$.next(null);

    this.isDevicesUpToDate$.next(false);
    this.inventoryTypeData$.next(null);
    this.inventoryStatusData$.next(null);

    this.companyInfo$.next(null);
  }
}
